/**
 * Browser-compatible logger implementation.
 *
 * @remarks
 * This module provides a lightweight, browser-compatible logging solution
 * that works in both Node.js and browser environments without Node.js-specific
 * dependencies like fs, path, or log4js.
 */
/**
 * Log level priorities (lower = more verbose).
 */
const LOG_LEVELS = {
    trace: 0,
    debug: 1,
    info: 2,
    warn: 3,
    error: 4,
    fatal: 5,
    silent: 6,
};
/**
 * Format a log message with timestamp and label.
 */
function formatMessage(level, label, message) {
    const now = new Date();
    const timestamp = now.toISOString().replace("T", " ").slice(0, 19);
    return `${timestamp} [${level.toUpperCase()}] ${label} - ${message}`;
}
/**
 * Simple browser-compatible logger implementation.
 */
class ConsoleLogger {
    label;
    _level;
    constructor(label, level = "info") {
        this.label = label;
        this._level = level;
    }
    get level() {
        return this._level;
    }
    set level(value) {
        const normalized = value.toLowerCase();
        if (normalized in LOG_LEVELS) {
            this._level = normalized;
        }
    }
    shouldLog(level) {
        return LOG_LEVELS[level] >= LOG_LEVELS[this._level];
    }
    trace(_message, ..._args) {
        if (this.shouldLog("trace")) {
        }
    }
    debug(_message, ..._args) {
        if (this.shouldLog("debug")) {
        }
    }
    info(_message, ..._args) {
        if (this.shouldLog("info")) {
        }
    }
    warn(_message, ..._args) {
        if (this.shouldLog("warn")) {
        }
    }
    error(message, ...args) {
        if (this.shouldLog("error")) {
            console.error(formatMessage("error", this.label, message), ...args);
        }
    }
    fatal(message, ...args) {
        if (this.shouldLog("fatal")) {
            console.error(formatMessage("fatal", this.label, message), ...args);
        }
    }
}
/**
 * Creates a configured logger instance for BD SDK modules.
 *
 * @remarks
 * This function provides a centralized logging solution for all BD SDK modules.
 * It works in both Node.js and browser environments using console-based logging.
 *
 * **Features:**
 * - **Flexible Log Levels**: Support for all standard log levels (trace, debug, info, warn, error, fatal)
 * - **Module Identification**: Each logger instance is labeled for easy identification
 * - **Browser Compatible**: No Node.js-specific dependencies
 * - **Thread-Safe**: Safe for concurrent usage across multiple modules
 *
 * **Usage Guidelines:**
 * - Use descriptive labels to identify the source module or component
 * - Set appropriate log levels based on environment (debug for dev, info for prod)
 * - Avoid logging sensitive information like API keys or user data
 *
 * @param options - Logger configuration options including label and level
 * @param _persistLogs - Deprecated parameter, kept for backward compatibility (ignored)
 * @returns Configured Logger instance ready for use
 *
 * @example
 * **Basic Console Logging**
 * ```typescript
 * import { getLogger } from "@blockdaemon/solana-staking-sdk";
 *
 * const logger = getLogger({
 *   label: "my-module",
 *   level: "info"
 * });
 *
 * logger.info("Module initialized successfully");
 * logger.debug("Debug information", { data: "example" });
 * logger.error("An error occurred", error);
 * ```
 *
 * @example
 * **Module-specific Logger Setup**
 * ```typescript
 * import { getLogger, Logger } from "@blockdaemon/solana-staking-sdk";
 *
 * class CommonModule {
 *   private logger: Logger;
 *
 *   constructor(options: { bdApiKey: string, logLevel?: string }) {
 *     this.logger = getLogger({
 *       label: "bd-sdk-common-module",
 *       level: options.logLevel || "info"
 *     });
 *
 *     this.logger.info("CommonModule initialized");
 *   }
 *
 *   async getBalance(address: string) {
 *     this.logger.debug("Fetching balance", { address });
 *     // ... implementation
 *     this.logger.info("Balance retrieved successfully");
 *   }
 * }
 * ```
 *
 * @example
 * **Environment-based Configuration**
 * ```typescript
 * import { getLogger } from "@blockdaemon/solana-staking-sdk";
 *
 * const isDevelopment = process.env.NODE_ENV === "development";
 *
 * const logger = getLogger({
 *   label: "bd-sdk-app",
 *   level: isDevelopment ? "debug" : "info"
 * });
 *
 * if (isDevelopment) {
 *   logger.debug("Development mode enabled");
 * }
 * ```
 *
 * @public
 */
export function getLogger(options, _persistLogs = false) {
    const level = (options.level || "info");
    return new ConsoleLogger(options.label, level);
}
/**
 * Masks sensitive query params & credentials in URLs for safe logging.
 *
 * Example: https://host/path?auth=secret&x=1 -> https://host/path?auth=****&x=1
 */
export function maskSensitiveInUrl(url) {
    const secretKeys = ["auth", "token", "apikey", "apiKey", "api_key", "authorization", "password"];
    try {
        const u = new URL(url);
        // Mask basic auth password if present
        if (u.password) {
            u.password = "****";
        }
        // Mask known secret query params
        for (const key of secretKeys) {
            if (u.searchParams.has(key)) {
                u.searchParams.set(key, "****");
            }
        }
        return u.toString();
    }
    catch {
        // Fallback for non-URL strings
        const pattern = new RegExp(`(${secretKeys.join("|")})=([^&]+)`, "gi");
        return url.replace(pattern, (_m, k) => `${k}=****`);
    }
}
//# sourceMappingURL=data:application/json;base64,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